import '@/config/axios-interceptor';
import { registerAppGlobalContext } from '@/utils/app-global-context';
import { isNetworkConnected } from '@/utils/network-status';
import { MenuButton } from '@/model/menubutton.model';
import AccountService from '@/services/account.service';
import CalendarService from '@/services/calendar.service';
import { RmxAudioPlayer } from "capacitor-plugin-playlist";
import QrcodeVue from 'qrcode.vue';
import { createApp, reactive } from 'vue';
import VueSlider from 'vue-slider-component';
import App from './App.vue';
import { GlobalContext } from './model/globalcontext.model';
import { PlayerBar } from './model/playerbar.model';
import { Release } from './model/release.model';
import router from './router';
import store from './store/index';

import { App as CapacitorApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { Network } from '@capacitor/network';
import { IonicVue } from '@ionic/vue';

// @ts-ignore
import MittEmitter from 'mitt-emitter';


/* Core CSS required for Ionic components to work properly */
import '@ionic/vue/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/vue/css/normalize.css';
import '@ionic/vue/css/structure.css';
import '@ionic/vue/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/vue/css/display.css';
import '@ionic/vue/css/flex-utils.css';
import '@ionic/vue/css/float-elements.css';
import '@ionic/vue/css/padding.css';
import '@ionic/vue/css/text-alignment.css';
import '@ionic/vue/css/text-transformation.css';

/* Theme variables */
import './theme/liga-slider.css';
import './theme/variables.css';

const globalContext = new GlobalContext();
registerAppGlobalContext(globalContext);
const accountService = new AccountService(globalContext);
const calendarService = new CalendarService();
calendarService.globalContext = globalContext;
const menuButton = new MenuButton('left');
const playerBar = new PlayerBar();
const release = new Release();
const emitter = new MittEmitter();
globalContext.isMobile = Capacitor.getPlatform() === 'ios' || Capacitor.getPlatform() === 'android';

Network.getStatus().then((status) => {
  if (!status.connected) {
    globalContext.offlineMode = true;
  }
})

const app = createApp(App)
  .use(IonicVue, { swipeBackEnabled: true, backButtonText: 'Назад' })
  .use(router)
  .use(store)
  .component('VueSlider', VueSlider)
  .component('QrcodeVue', QrcodeVue)
  .provide(
    'globalContext', globalContext
  )
  .provide(
    'accountService', reactive(accountService),
  )
  .provide(
    'calendarService', reactive(calendarService),
  )
  .provide(
    'menuButton', reactive(menuButton),
  )
  .provide(
    'playerBar', reactive(playerBar),
  )
  .provide(
    'release', reactive(release),
  )
  .provide(
    'emitter', emitter
  );

if (globalContext.isMobile) {
  const audioPlayer = new RmxAudioPlayer();
  audioPlayer.initialize();
  audioPlayer.setLoop(false);
  audioPlayer.setOptions({
    resetStreamOnPause: false,
    options: {
      icon: "icon",
    },
  });

  app.provide(
    'audioPlayer', audioPlayer
  );
}

router.isReady().then(() => {
  app.mount('#app');

  // Deep link после оплаты: ligateam://payment-success?eventId=123 → страница успеха в приложении
  if (globalContext.isMobile) {
    CapacitorApp.addListener('appUrlOpen', (ev: { url?: string }) => {
      const url = ev.url || '';
      if (!url.startsWith('ligateam://payment-success')) return;
      try {
        const q = url.indexOf('?');
        const query = q >= 0 ? url.slice(q) : '';
        const params = new URLSearchParams(query);
        const eventId = params.get('eventId');
        const purchaseId = params.get('purchaseId');
        const releaseId = params.get('releaseId');
        if (purchaseId && releaseId) {
          router.push(`/events/success?releasePaid=1&purchaseId=${encodeURIComponent(purchaseId)}&releaseId=${encodeURIComponent(releaseId)}`);
        } else if (purchaseId) {
          router.push(`/events/success?releasePaid=1&purchaseId=${encodeURIComponent(purchaseId)}`);
        } else if (eventId) {
          router.push(`/events/success?eventId=${encodeURIComponent(eventId)}`);
        } else {
          router.push('/events/success');
        }
      } catch (_) {
        router.push('/events/success');
      }
    });
  }
});

router.beforeEach(async (to, from, next) => {
  const adminRoutes = ['Users', 'AdminRoles', 'AdminEducationSections', 'AdminVideoUpload'];

  if (to.name === 'BadConnection' || to.name === 'SignUp' || to.name === 'ResetPassword' || to.name === 'Login' || to.name === 'PrivacyPolicy' || to.name === 'OfferSupplement' || to.name === 'Showcase') {
    if (to.name === 'BadConnection') {
      globalContext.prevRoute = from.fullPath;
    }
    next();
    return;
  }

  if (adminRoutes.includes(to.name as string)) {
    if (!accountService.authenticated && !globalContext.offlineMode) {
      try {
        const restored = await accountService.retrieveAccount();
        if (!restored) {
          const onlineAdmin = await isNetworkConnected();
          next(onlineAdmin ? { name: 'Showcase' } : { name: 'Login' });
          return;
        }
      } catch (e) {
        console.error('Restore session:', e);
        const onlineAdminCatch = await isNetworkConnected();
        next(onlineAdminCatch ? { name: 'Showcase' } : { name: 'Login' });
        return;
      }
    }
    try {
      const hasAccess = await accountService.hasAnyAuthorityAndCheckAuth('ROLE_MANAGER');
      if (hasAccess) {
        next();
      } else {
        console.warn(`Access denied to ${String(to.name)}: ROLE_MANAGER required`);
        next('/today');
      }
    } catch (err) {
      console.error('Error checking access:', err);
      const onlineAdminErr = await isNetworkConnected();
      next(onlineAdminErr ? { name: 'Showcase' } : { name: 'Login' });
    }
    return;
  }

  if (to.name !== 'Login' && to.name !== 'Today' && to.name !== 'PrivacyPolicy' && to.name !== 'OfferSupplement' && to.name !== 'Showcase' && !accountService.authenticated && !globalContext.offlineMode) {
    try {
      const restored = await accountService.retrieveAccount();
      if (restored && accountService.authenticated) {
        next();
        return;
      }
    } catch (e) {
      console.error('Restore session:', e);
    }
    const online = await isNetworkConnected();
    next(online ? { name: 'Showcase' } : { name: 'Login' });
    return;
  }

  next();
});